/**
 * Statistiky tisku. Stejný obsah má záložka v modulu a aplikace Tisky.
 */
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ArrowLeft,
  Banknote,
  Download,
  FileDown,
  FileText,
  List,
  Printer,
  RefreshCw,
  Users,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  FilterChips,
  Input,
  Label,
  Select,
  StatCard,
  StatsGrid,
  Toggle,
} from '../../components/ui';
import type { BadgeVariant, DataTableColumn, SortState } from '../../components/ui';
import { api } from '../../lib/api';
import { explainPrintError, PrintCallError, unwrap } from './print-stats-call';
import { PrinterPricingCard, PRINTERS_SECTION_ID, usePrinterSettings } from './print-pricing';
import { ExportDialog, type ExportKind, GROUPS, PeriodTiles } from './print-export';
import {
  chooseCustomer,
  DEFAULT_CUSTOMER_PREF,
  DEFAULT_JOB_SORT,
  formatMoney,
  jobSortArgs,
  mergeCustomers,
  type PeriodId,
  periodRange,
  printEventStale,
  sharePct,
  SHOW_COST_PREF,
  showCustomerPicker,
  syncAdded,
  type SyncAgo,
  syncAgo,
} from './print-stats-logic';
import type {
  PrintCustomersResult,
  PrintFilters,
  PrintGroupBy,
  PrintIngestResult,
  PrintJobRow,
  PrintJobsResult,
  PrintServerStatus,
  PrintSourcesResult,
  PrintStatRow,
  PrintStatsResult,
  PrintSyncStatus,
  ToolFail,
} from './print-stats-types';

const PAGE_SIZE = 50;

function ShareBar({ part, total }: { part: number; total: number }) {
  const pct = sharePct(part, total);
  return (
    <div className='flex items-center gap-2'>
      <div className='h-2 w-24 overflow-hidden rounded-full bg-gray-800'>
        <div className='h-full bg-plex' style={{ width: `${pct}%` }} />
      </div>
      <span className='w-8 text-xs tabular-nums text-gray-400'>{pct}%</span>
    </div>
  );
}

export function PrintStatsPanel({ mode }: { mode: 'module' | 'app' }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [period, setPeriod] = useState<PeriodId>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [groupBy, setGroupBy] = useState<PrintGroupBy>('user');
  const [view, setView] = useState<'summary' | 'jobs'>('summary');
  const [jobUser, setJobUser] = useState<string | null>(null);
  const [jobPrinter, setJobPrinter] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [jobSort, setJobSort] = useState<SortState>(DEFAULT_JOB_SORT);
  const [offset, setOffset] = useState(0);
  const [customer, setCustomer] = useState('');
  const [hintCustomers, setHintCustomers] = useState<string[]>([]);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [exportKind, setExportKind] = useState<ExportKind | null>(null);
  /** Osobní export: login uživatele z řádku (null = celá organizace). */
  const [exportUser, setExportUser] = useState<string | null>(null);
  /** Ruční volba „Zobrazit ceny" (null = výchozí ze serveru). */
  const [showCostChoice, setShowCostChoice] = useState<boolean | null>(null);
  const [costDefault, setCostDefault] = useState<{ show: boolean; configured: boolean } | null>(
    null,
  );
  const [syncError, setSyncError] = useState<string | null>(null);

  const range = useMemo(
    () => periodRange(period, new Date(), { from: customFrom, to: customTo }),
    [period, customFrom, customTo],
  );

  // Zákazníci z print_customers (agenti flotily + tisky), ne z fleet.customer_list:
  // acm.fleet_customers bývá na uzlu zákazníka prázdná a výběr pak zůstal prázdný.
  // Aplikace Tisky výběr nikdy neukazuje; použije jen výchozího zákazníka (držitel
  // aplikace dostane svého jediného, správce svou výchozí volbu). Bez něj rozhodne server.
  const customersQ = useQuery({
    queryKey: ['printserver', 'customers'],
    staleTime: 60_000,
    queryFn: async () =>
      unwrap(
        await api.mcp<PrintCustomersResult | ToolFail>('printserver.print_customers', {}, {
          silent: true,
        }),
        t('printserver.stats.loadFailed'),
      ),
  });
  const customers = useMemo(
    () => mergeCustomers(customersQ.data?.customers ?? [], hintCustomers),
    [customersQ.data, hintCustomers],
  );
  const selected = chooseCustomer(customer, customers, customersQ.data?.default_customer);
  const userDefault = customersQ.data?.user_default ?? null;
  const showPicker = showCustomerPicker(mode, customers.length);
  const ready = !!selected ||
    (mode === 'app' && (customersQ.isSuccess || customersQ.isError));
  const scope = selected ? { customer: selected } : {};
  const scopeKey = scope.customer ?? '';

  const saveDefault = useMutation({
    mutationFn: async (id: string) => {
      const result = await api.mcp<{ ok?: boolean; error?: string }>(
        'ui.prefs_set',
        { key: DEFAULT_CUSTOMER_PREF, value: { customer: id } },
        { silent: true },
      );
      if (!result || result.ok !== true) throw new Error(result?.error || 'save_failed');
      return id;
    },
    onSuccess: (id) => {
      setDefaultError(null);
      qc.setQueryData<PrintCustomersResult>(
        ['printserver', 'customers'],
        (current) =>
          current
            ? { ...current, user_default: id, default_customer: id, default_source: 'user' }
            : current,
      );
    },
    onError: () => setDefaultError(t('printserver.stats.defaultFailed')),
  });

  // customer_required: v modulu znovu načíst seznam (a doplnit zákazníky z nápovědy);
  // v aplikaci se jen ukáže hláška — výběr tam není.
  const noteCustomer = (error: unknown) => {
    if (!(error instanceof PrintCallError) || error.code !== 'customer_required') return;
    if (mode !== 'module') return;
    void qc.invalidateQueries({ queryKey: ['printserver', 'customers'] });
    if (error.customers.length === 0) return;
    setHintCustomers((current) => [...new Set([...current, ...error.customers])]);
  };

  const sourcesQ = useQuery({
    queryKey: ['printserver', 'sources', mode, scopeKey],
    enabled: ready,
    queryFn: async () => {
      return unwrap(
        await api.mcp<PrintSourcesResult | ToolFail>('printserver.print_sources', scope, {
          silent: true,
        }),
        t('printserver.stats.loadFailed'),
      );
    },
  });

  const statsQ = useQuery({
    queryKey: ['printserver', 'stats', mode, scopeKey, range.from, range.to, groupBy],
    enabled: ready && view === 'summary',
    queryFn: async () => {
      const input = { ...scope, from: range.from, to: range.to, group_by: groupBy, limit: 100 };
      return unwrap(
        await api.mcp<PrintStatsResult | ToolFail>('printserver.print_stats', input, {
          silent: true,
        }),
        t('printserver.stats.loadFailed'),
      );
    },
  });

  const filters: PrintFilters = {
    ...(jobUser ? { user: jobUser } : {}),
    ...(jobPrinter ? { printer: jobPrinter } : {}),
  };
  const jobsQ = useQuery({
    queryKey: [
      'printserver',
      'jobs',
      mode,
      scopeKey,
      range.from,
      range.to,
      jobUser,
      jobPrinter,
      q,
      jobSort.key,
      jobSort.dir,
      offset,
    ],
    enabled: ready && view === 'jobs',
    // Při psaní do hledání / změně řazení zůstanou vidět předchozí řádky (a pole hledání
    // neztratí fokus), dokud nedorazí nové.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const input = {
        ...scope,
        from: range.from,
        to: range.to,
        filters,
        q,
        ...jobSortArgs(jobSort),
        offset,
        limit: PAGE_SIZE,
      };
      return unwrap(
        await api.mcp<PrintJobsResult | ToolFail>('printserver.print_jobs', input, {
          silent: true,
        }),
        t('printserver.stats.loadFailed'),
      );
    },
  });

  // Výchozí stav „Zobrazit ceny" ze serveru (osobní volba, jinak výchozí zákazníka).
  useEffect(() => {
    if (statsQ.data?.show_cost === undefined) return;
    setCostDefault({ show: statsQ.data.show_cost, configured: !!statsQ.data.prices_configured });
  }, [statsQ.data]);
  const showCost = showCostChoice ?? costDefault?.show ?? false;
  const toggleShowCost = (value: boolean) => {
    setShowCostChoice(value);
    if (!value && jobSort.key === 'cost') setJobSort(DEFAULT_JOB_SORT);
    // Zapamatovat pro příště (per uživatel); chyba uložení nevadí.
    void api.mcp('ui.prefs_set', { key: SHOW_COST_PREF, value: { show: value } }, {
      silent: true,
    }).catch(() => undefined);
  };
  const pricingQ = usePrinterSettings(scope, scopeKey, mode === 'module' && ready);
  const canSetPrices = mode === 'module' && !!pricingQ.data?.can_edit;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['printserver', 'stats'] });
    void qc.invalidateQueries({ queryKey: ['printserver', 'jobs'] });
    void qc.invalidateQueries({ queryKey: ['printserver', 'sources'] });
  };

  const ingest = useMutation({
    mutationFn: async (agentId?: string) => {
      const input = { ...scope, ...(agentId ? { agent_id: agentId } : {}) };
      return unwrap(
        await api.mcp<PrintIngestResult | ToolFail>(
          'printserver.print_ingest',
          input,
          { silent: true },
        ),
        t('printserver.stats.syncFailed'),
      );
    },
    onSuccess: () => {
      setSyncError(null);
      refresh();
    },
    onError: (error: Error) => {
      noteCustomer(error);
      setSyncError(explainPrintError(t, error, t('printserver.stats.syncFailed')));
    },
  });

  useEffect(() => {
    noteCustomer(sourcesQ.error);
    noteCustomer(statsQ.error);
    noteCustomer(jobsQ.error);
  }, [sourcesQ.error, statsQ.error, jobsQ.error]);

  const sources = sourcesQ.data;
  const pending = !!sources?.servers.some((server) => server.pending);
  const added = syncAdded(sources?.last_sync_at ?? null, sources?.servers ?? []);
  const ago = syncAgo(sources?.last_sync_at ?? null, new Date());
  const status = sources?.status ?? 'never';
  const errorText = (sources?.servers ?? []).map((server) => server.last_error).filter(Boolean)
    .join('\n');

  const groupLabel = (id: PrintGroupBy) => t(`printserver.stats.group.${id}`);
  const openExport = (kind: ExportKind, user: string | null = null) => {
    setExportUser(user);
    setExportKind(kind);
  };

  const openJobs = (row: PrintStatRow) => {
    if (!row.key) return;
    if (groupBy === 'user') setJobUser(row.key);
    else if (groupBy === 'printer') setJobPrinter(row.key);
    else return;
    setOffset(0);
    setView('jobs');
  };

  return (
    <div className='space-y-4'>
      {showPicker && (
        <div className='flex flex-wrap items-end gap-2'>
          <div className='w-56'>
            <Label>{t('printserver.stats.customer')}</Label>
            <Select
              size='md'
              value={selected}
              onChange={(event) => setCustomer(event.target.value)}
              options={customers.map((item) => ({ value: item.id, label: item.label }))}
              placeholder={t('printserver.stats.pickCustomer')}
            />
          </div>
          {selected && selected === userDefault
            ? (
              <span className='pb-2'>
                <Badge variant='success'>{t('printserver.stats.isDefault')}</Badge>
              </span>
            )
            : selected && (
              <Button
                size='md'
                variant='secondary'
                loading={saveDefault.isPending}
                onClick={() => saveDefault.mutate(selected)}
              >
                {t('printserver.stats.setDefault')}
              </Button>
            )}
        </div>
      )}
      <div>
        <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
          <Label className='mb-0'>{t('printserver.stats.period')}</Label>
          <Toggle
            label={t('printserver.stats.showCost')}
            value={showCost}
            onSave={toggleShowCost}
          />
        </div>
        <PeriodTiles
          label={t('printserver.stats.period')}
          value={period}
          onChange={setPeriod}
          customFrom={customFrom}
          customTo={customTo}
        />
      </div>
      {showCost && costDefault && !costDefault.configured && (
        <div className='flex flex-wrap items-center gap-2 text-xs text-gray-400'>
          <span>{t('printserver.stats.noPrices')}</span>
          {canSetPrices && (
            <Button
              size='sm'
              variant='ghost'
              onClick={() =>
                document.getElementById(PRINTERS_SECTION_ID)?.scrollIntoView({
                  behavior: 'smooth',
                })}
            >
              {t('printserver.stats.setPrices')}
            </Button>
          )}
        </div>
      )}

      {period === 'custom' && (
        <div className='flex flex-wrap gap-3'>
          <div>
            <Label>{t('printserver.stats.from')}</Label>
            <Input
              size='md'
              type='date'
              value={customFrom}
              onChange={(event) => setCustomFrom(event.target.value)}
            />
          </div>
          <div>
            <Label>{t('printserver.stats.to')}</Label>
            <Input
              size='md'
              type='date'
              value={customTo}
              onChange={(event) => setCustomTo(event.target.value)}
            />
          </div>
        </div>
      )}

      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-sm text-gray-300'>
          {t('printserver.stats.syncLast', { when: agoText(t, ago) })}
        </span>
        <span title={status === 'error' ? errorText : undefined}>
          <Badge variant={statusVariant(status, pending)} dot>
            {statusText(t, status, pending)}
          </Badge>
        </span>
        {added != null && status === 'ok' && !pending && (
          <span className='text-xs text-gray-400'>
            {t('printserver.stats.syncAdded', { count: added })}
          </span>
        )}
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            size='md'
            variant='secondary'
            icon={RefreshCw}
            loading={ingest.isPending && !ingest.variables}
            disabled={!ready || ingest.isPending}
            onClick={() => ingest.mutate(undefined)}
          >
            {t('printserver.stats.syncNow')}
          </Button>
          <Button
            size='md'
            variant='secondary'
            icon={FileDown}
            onClick={() => openExport('pdf')}
          >
            {t('printserver.stats.downloadPdf')}
          </Button>
          <Button
            size='md'
            variant='secondary'
            icon={Download}
            disabled={!ready}
            onClick={() => openExport('csv')}
          >
            {t('printserver.stats.exportCsv')}
          </Button>
        </div>
      </div>
      {syncError && <Alert variant='destructive'>{syncError}</Alert>}
      {defaultError && <Alert variant='destructive'>{defaultError}</Alert>}

      {!ready && customersQ.isLoading && (
        <p className='text-sm text-gray-400'>{t('printserver.stats.loading')}</p>
      )}

      {!ready && customersQ.isError && (
        <EmptyState
          title={t('printserver.stats.loadFailed')}
          description={explainPrintError(
            t,
            customersQ.error,
            t('printserver.stats.loadFailed'),
          )}
        />
      )}

      {!ready && customersQ.isSuccess && (
        <EmptyState
          title={customers.length === 0
            ? t('printserver.stats.errNoCustomer')
            : t('printserver.stats.pickCustomer')}
        />
      )}

      {ready && (statsQ.isError || jobsQ.isError || sourcesQ.isError) && (
        <EmptyState
          title={t('printserver.stats.loadFailed')}
          description={explainPrintError(
            t,
            statsQ.error || jobsQ.error || sourcesQ.error,
            t('printserver.stats.loadFailed'),
          )}
        />
      )}

      {ready && view === 'summary' && (
        <>
          {/* Šest karet: na desktopu v jedné řadě (StatsGrid columns=4 by pátou zalomil). */}
          <StatsGrid
            columns={4}
            className={showCost ? 'lg:grid-cols-6 xl:grid-cols-6' : 'lg:grid-cols-5 xl:grid-cols-5'}
          >
            <StatCard
              label={t('printserver.stats.pages')}
              value={statsQ.data?.totals.total_pages ?? '—'}
              icon={<FileText />}
              sub={statsQ.data && statsQ.data.totals.unknown_color_pages > 0
                ? t('printserver.stats.unknownColor', {
                  count: statsQ.data.totals.unknown_color_pages,
                })
                : undefined}
            />
            <StatCard
              label={t('printserver.stats.color')}
              value={statsQ.data?.totals.color_pages ?? '—'}
              variant='info'
            />
            <StatCard
              label={t('printserver.stats.mono')}
              value={statsQ.data?.totals.mono_pages ?? '—'}
            />
            <StatCard
              label={t('printserver.stats.jobs')}
              value={statsQ.data?.totals.jobs ?? '—'}
              icon={<Printer />}
            />
            <StatCard
              label={t('printserver.stats.users')}
              value={statsQ.data?.totals.users ?? '—'}
              icon={<Users />}
            />
            {showCost && (
              <StatCard
                label={t('printserver.stats.totalCost')}
                value={statsQ.data
                  ? formatMoney(
                    statsQ.data.totals.total_cost ?? 0,
                    i18n.language,
                    statsQ.data.totals.currency,
                  )
                  : '—'}
                icon={<Banknote />}
                variant='success'
              />
            )}
          </StatsGrid>

          <div className='flex flex-wrap items-center justify-between gap-2'>
            <FilterChips
              label={t('printserver.stats.groupBy')}
              value={groupBy}
              onChange={setGroupBy}
              options={GROUPS.map((id) => ({ value: id, label: groupLabel(id) }))}
            />
            <Button
              size='md'
              variant='secondary'
              icon={List}
              onClick={() => {
                setJobUser(null);
                setJobPrinter(null);
                setOffset(0);
                setView('jobs');
              }}
            >
              {t('printserver.stats.showJobs')}
            </Button>
          </div>

          {statsQ.isError || statsQ.isLoading
            ? statsQ.isLoading
              ? <p className='text-sm text-gray-400'>{t('printserver.stats.loading')}</p>
              : null
            : (statsQ.data?.rows.length ?? 0) === 0
            ? <EmptyState title={t('printserver.stats.empty')} />
            : (
              <DataTable
                key={groupBy}
                data={(statsQ.data?.rows ?? []) as Array<PrintStatRow & Record<string, unknown>>}
                rowKey={(row) => row.key || row.label}
                onRowClick={groupBy === 'user' || groupBy === 'printer' ? openJobs : undefined}
                searchable
                searchPlaceholder={t('printserver.stats.searchStats')}
                searchText={(row) => `${nameOf(t, groupBy, row)} ${row.key}`}
                columns={[
                  {
                    key: 'label',
                    label: t('printserver.stats.colName'),
                    render: (row) => nameOf(t, groupBy, row),
                    sortable: true,
                    sortValue: (row) => groupBy === 'day' ? row.key : nameOf(t, groupBy, row),
                  },
                  {
                    key: 'share',
                    label: t('printserver.stats.colShare'),
                    render: (row) => (
                      <ShareBar
                        part={row.total_pages}
                        total={statsQ.data?.totals.total_pages ?? 0}
                      />
                    ),
                    sortable: true,
                    sortValue: (row) => row.total_pages,
                  },
                  { key: 'total_pages', label: t('printserver.stats.colPages'), sortable: true },
                  { key: 'jobs', label: t('printserver.stats.colJobs'), sortable: true },
                  { key: 'color_pages', label: t('printserver.stats.colColor'), sortable: true },
                  { key: 'mono_pages', label: t('printserver.stats.colMono'), sortable: true },
                  ...(showCost
                    ? [{
                      key: 'cost',
                      label: t('printserver.stats.colCost'),
                      render: (row: PrintStatRow) =>
                        formatMoney(row.cost ?? 0, i18n.language, statsQ.data?.totals.currency),
                      sortable: true,
                      sortValue: (row: PrintStatRow) => row.cost ?? 0,
                    }]
                    : []),
                  ...(groupBy === 'user'
                    ? [{
                      key: 'personal',
                      label: t('printserver.stats.colAction'),
                      render: (row: PrintStatRow) =>
                        row.key
                          ? (
                            <PersonalReportButton
                              label={t('printserver.stats.personalReport')}
                              onClick={() => openExport('pdf', row.key)}
                            />
                          )
                          : null,
                    }]
                    : []),
                ]}
              />
            )}
        </>
      )}

      {ready && view === 'jobs' && (
        <>
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              size='md'
              variant='secondary'
              icon={ArrowLeft}
              onClick={() => {
                setView('summary');
                setJobUser(null);
                setJobPrinter(null);
                setQ('');
                setJobSort(DEFAULT_JOB_SORT);
                setOffset(0);
              }}
            >
              {t('printserver.stats.backToSummary')}
            </Button>
            {jobUser && (
              <Badge variant='info'>{t('printserver.stats.filterUser', { name: jobUser })}</Badge>
            )}
            {jobPrinter && (
              <Badge variant='info'>
                {t('printserver.stats.filterPrinter', { name: jobPrinter })}
              </Badge>
            )}
          </div>
          <h3 className='text-sm font-medium'>{t('printserver.stats.jobsTitle')}</h3>
          {
            /* Hledání (q) i řazení (sort/order) dělá server; tabulka zůstává vidět i bez výsledků,
              ať jde hledání upravit. */
          }
          {!jobsQ.isError && (
            <DataTable
              data={(jobsQ.data?.rows ?? []) as Array<PrintJobRow & Record<string, unknown>>}
              rowKey={(row, index) => `${row.printed_at}-${row.printer}-${index}`}
              columns={jobColumns(t, i18n.language, showCost, (user) => openExport('pdf', user))}
              emptyMessage={jobsQ.isLoading
                ? t('printserver.stats.loading')
                : t('printserver.stats.empty')}
              sort={jobSort}
              onSortChange={(next) => {
                setJobSort(next);
                setOffset(0);
              }}
              searchable
              searchPlaceholder={t('printserver.stats.searchJobs')}
              search={q}
              onSearchChange={(value) => {
                setQ(value);
                setOffset(0);
              }}
            />
          )}
          <JobPager
            offset={offset}
            total={jobsQ.data?.total ?? 0}
            shown={jobsQ.data?.rows.length ?? 0}
            onPrev={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
            onNext={() => setOffset((current) => current + PAGE_SIZE)}
          />
        </>
      )}

      {mode === 'module' && ready && (
        <Card>
          <CardContent>
            <h3 className='mb-3 text-sm font-medium'>{t('printserver.stats.servers')}</h3>
            {(sources?.servers.length ?? 0) === 0
              ? <EmptyState title={t('printserver.stats.noServers')} />
              : (
                <DataTable
                  data={(sources?.servers ?? []) as Array<
                    PrintServerStatus & Record<string, unknown>
                  >}
                  rowKey={(row) => row.agent_id || row.server}
                  columns={serverColumns(
                    t,
                    i18n.language,
                    ingest.isPending,
                    ingest.variables,
                    (agentId) => {
                      ingest.mutate(agentId);
                    },
                  )}
                />
              )}
          </CardContent>
        </Card>
      )}

      {mode === 'module' && ready && <PrinterPricingCard scope={scope} scopeKey={scopeKey} />}

      <ExportDialog
        kind={exportKind}
        onClose={() => setExportKind(null)}
        mode={mode}
        scope={scope}
        scopeKey={scopeKey}
        ready={ready}
        showPicker={showPicker}
        customers={customers}
        selected={selected}
        onCustomer={setCustomer}
        initial={{
          period,
          from: range.from,
          to: range.to,
          groupBy,
          user: exportUser,
          includeCost: showCost,
        }}
        pageFilters={filters}
        onCallError={noteCustomer}
      />
    </div>
  );
}

function JobPager({
  offset,
  total,
  shown,
  onPrev,
  onNext,
}: {
  offset: number;
  total: number;
  shown: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  if (total <= PAGE_SIZE) return null;
  const from = shown === 0 ? 0 : offset + 1;
  const to = offset + shown;
  return (
    <div className='flex items-center justify-between gap-2'>
      <span className='text-xs text-gray-400'>
        {t('printserver.stats.pageOf', { from, to, total })}
      </span>
      <div className='flex gap-2'>
        <Button size='md' variant='secondary' disabled={offset === 0} onClick={onPrev}>
          {t('printserver.stats.prev')}
        </Button>
        <Button size='md' variant='secondary' disabled={to >= total} onClick={onNext}>
          {t('printserver.stats.next')}
        </Button>
      </div>
    </div>
  );
}

function agoText(t: TFunction, ago: SyncAgo): string {
  if (ago.kind === 'never') return t('printserver.stats.syncNever');
  if (ago.kind === 'now') return t('printserver.stats.justNow');
  if (ago.kind === 'min') return t('printserver.stats.agoMin', { count: ago.count });
  if (ago.kind === 'hour') return t('printserver.stats.agoHour', { count: ago.count });
  return t('printserver.stats.agoDay', { count: ago.count });
}

function statusText(t: TFunction, status: PrintSyncStatus, pending: boolean): string {
  if (status === 'error') return t('printserver.stats.syncError');
  if (status === 'running') return t('printserver.stats.syncRunning');
  if (status === 'never') return t('printserver.stats.syncNever');
  if (pending) return t('printserver.stats.syncPending');
  return t('printserver.stats.syncOk');
}

function statusVariant(status: PrintSyncStatus, pending: boolean): BadgeVariant {
  if (status === 'error') return 'error';
  if (status === 'running') return 'info';
  if (status === 'never') return 'default';
  if (pending) return 'warning';
  return 'success';
}

function nameOf(t: TFunction, groupBy: PrintGroupBy, row: PrintStatRow): string {
  if (groupBy === 'color_mode') {
    if (row.key === 'color') return t('printserver.stats.colorColor');
    if (row.key === 'mono') return t('printserver.stats.colorMono');
    return t('printserver.stats.colorUnknown');
  }
  if (!row.key && groupBy !== 'day') return t('printserver.stats.unknown');
  return row.label || row.key || t('printserver.stats.unknown');
}

function fmtWhen(iso: string | null, language: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const loc = language.startsWith('en') ? 'en-GB' : language.startsWith('it') ? 'it-IT' : 'cs-CZ';
  return date.toLocaleString(loc, { dateStyle: 'short', timeStyle: 'short' });
}

/** Klíče řazených sloupců = hodnoty `sort` toolu print_jobs (řadí server). */
/** Malé tlačítko „Osobní report" v řádku tabulky (neotevře detail řádku). */
function PersonalReportButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      size='sm'
      variant='secondary'
      icon={FileDown}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {label}
    </Button>
  );
}

function jobColumns(
  t: TFunction,
  language: string,
  showCost: boolean,
  onPersonal: (user: string) => void,
): DataTableColumn<PrintJobRow & Record<string, unknown>>[] {
  return [
    {
      key: 'printed_at',
      label: t('printserver.stats.colWhen'),
      render: (row: PrintJobRow) => fmtWhen(row.printed_at, language),
      sortable: true,
    },
    {
      key: 'user',
      label: t('printserver.stats.colUser'),
      render: (row: PrintJobRow) => row.label || row.user_name || '—',
      sortable: true,
    },
    { key: 'printer', label: t('printserver.stats.colPrinter'), sortable: true },
    {
      key: 'document',
      label: t('printserver.stats.colDocument'),
      render: (row: PrintJobRow) => row.document || '—',
    },
    { key: 'total_pages', label: t('printserver.stats.colPages'), sortable: true },
    { key: 'copies', label: t('printserver.stats.colCopies') },
    {
      key: 'paper_size',
      label: t('printserver.stats.colPaper'),
      render: (row: PrintJobRow) => row.paper_size || t('printserver.stats.unknown'),
      sortable: true,
    },
    {
      key: 'color_mode',
      label: t('printserver.stats.colColor'),
      sortable: true,
      render: (row: PrintJobRow) =>
        row.color_mode === 'color'
          ? t('printserver.stats.colorColor')
          : row.color_mode === 'mono'
          ? t('printserver.stats.colorMono')
          : t('printserver.stats.colorUnknown'),
    },
    {
      key: 'duplex',
      label: t('printserver.stats.colDuplex'),
      render: (row: PrintJobRow) =>
        row.duplex == null
          ? '—'
          : row.duplex
          ? t('printserver.stats.yes')
          : t('printserver.stats.no'),
    },
    { key: 'server', label: t('printserver.stats.colServer') },
    ...(showCost
      ? [{
        key: 'cost',
        label: t('printserver.stats.colCost'),
        render: (row: PrintJobRow) => formatMoney(row.cost ?? 0, language),
        sortable: true,
      }]
      : []),
    {
      key: 'personal',
      label: t('printserver.stats.colAction'),
      render: (row: PrintJobRow) =>
        row.user_name
          ? (
            <PersonalReportButton
              label={t('printserver.stats.personalReport')}
              onClick={() => onPersonal(row.user_name!)}
            />
          )
          : null,
    },
  ];
}

function serverColumns(
  t: TFunction,
  language: string,
  pending: boolean,
  current: string | undefined,
  onSync: (agentId: string) => void,
): DataTableColumn<PrintServerStatus & Record<string, unknown>>[] {
  return [
    { key: 'server', label: t('printserver.stats.colServer'), sortable: true },
    {
      key: 'sources',
      label: t('printserver.stats.colSources'),
      render: (row: PrintServerStatus) => row.sources.join(', '),
      sortable: true,
      sortValue: (row: PrintServerStatus) => row.sources.join(', '),
    },
    {
      key: 'last_event_at',
      label: t('printserver.stats.colLastPrint'),
      sortable: true,
      render: (row: PrintServerStatus) => (
        <span className='inline-flex items-center gap-2'>
          {fmtWhen(row.last_event_at, language)}
          {printEventStale(row.last_event_at, new Date()) && (
            <Badge variant='warning'>{t('printserver.stats.stale')}</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'last_run_at',
      label: t('printserver.stats.colLastSync'),
      sortable: true,
      render: (row: PrintServerStatus) => {
        const trigger = row.last_run_trigger === 'push'
          ? t('printserver.stats.triggerPush')
          : row.last_run_trigger === 'on_demand'
          ? t('printserver.stats.triggerOnDemand')
          : row.last_run_trigger === 'manual'
          ? t('printserver.stats.triggerManual')
          : '';
        return `${fmtWhen(row.last_run_at, language)}${trigger ? ` · ${trigger}` : ''}`;
      },
    },
    { key: 'last_run_added', label: t('printserver.stats.colAdded'), sortable: true },
    {
      key: 'last_run_status',
      label: t('printserver.stats.colStatus'),
      sortable: true,
      sortValue: (row: PrintServerStatus) =>
        row.last_run_status === 'error' ? 0 : row.pending ? 1 : 2,
      render: (row: PrintServerStatus) => (
        <span title={row.last_error ?? undefined}>
          <Badge
            variant={row.last_run_status === 'error'
              ? 'error'
              : row.pending
              ? 'warning'
              : 'success'}
          >
            {row.last_run_status === 'error'
              ? t('printserver.stats.syncError')
              : row.pending
              ? t('printserver.stats.syncPending')
              : t('printserver.stats.syncOk')}
          </Badge>
        </span>
      ),
    },
    {
      key: 'action',
      label: t('printserver.stats.colAction'),
      render: (row: PrintServerStatus) => (
        <Button
          size='sm'
          variant='secondary'
          icon={RefreshCw}
          loading={pending && current === row.agent_id}
          disabled={pending}
          onClick={(event) => {
            event.stopPropagation();
            onSync(row.agent_id);
          }}
        >
          {t('printserver.stats.syncServer')}
        </Button>
      ),
    },
  ];
}
